import { useMemo, useState } from "react";

import {
  makeRunKey,
  useCancelRun,
  useCapabilities,
  useCost,
  useCreateRun,
  useCurrentProjectId,
  useModelSettings,
  useRetryRun,
  useRuns,
} from "@/api/queries";
import type { Run } from "@/api/types";
import { Button } from "@/components/Button";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { Field, TextInput } from "@/components/Field";
import { Modal } from "@/components/Modal";
import { Panel } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { RunDetail } from "@/components/RunDetail";
import { Select } from "@/components/Select";
import { RUN_FINAL_STATUSES, RunStateLabel } from "@/components/StatusLabel";
import { formatNumber } from "@/lib/cn";
import { providerLabel, runtimeBlocksGeneration, runtimeNote } from "@/lib/model-runtime";
import { useUiStore } from "@/store/ui";

/** 任务筛选：按运行终态归并，取值与后端 `RunStatus` 对齐。 */
type RunFilter = "all" | "active" | "review" | "failed";

const FILTERS: { value: RunFilter; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "active", label: "进行中" },
  { value: "review", label: "待审核" },
  { value: "failed", label: "失败" },
];

const ACTIVE_STATUSES = new Set(["QUEUED", "RUNNING", "VALIDATING"]);

function matchesFilter(run: Run, filter: RunFilter): boolean {
  if (filter === "all") return true;
  if (filter === "active") return ACTIVE_STATUSES.has(run.status);
  if (filter === "review") return run.status === "WAITING_FOR_REVIEW";
  return run.status === "RETRYABLE_FAILED" || run.status === "BLOCKED" || run.status === "REJECTED";
}

/**
 * 「输入」列：`RunOut.inputs`，**只含调用方自由输入的参数**。
 *
 * 任务名不在这里——它已经是一个顶层字段（`run.task`，ADR-015），有自己的列。
 * 历史上它被 `execute()` 合并塞进 `inputs`，于是这一列会渲染成
 * `scope=single · task=… · artifact_ids=[…]`：任务名被塞在"输入"表头下、
 * 和框架键挤在一行、还被截断。那段历史见 ADR-015。
 */
function summarizeInputs(inputs: Record<string, unknown>): string {
  const entries = Object.entries(inputs);
  if (entries.length === 0) return "—";
  return entries
    .map(
      ([key, value]) =>
        `${key}=${typeof value === "object" ? JSON.stringify(value) : String(value)}`,
    )
    .join(" · ")
    .slice(0, 60);
}

/**
 * 生成中心（原型 `renderGenerate`）。
 *
 * 能力清单来自 `GET /capabilities`（AC-1406：不硬编码能力名），
 * 任务与额度来自 `GET /projects/{id}/runs` 与 `/cost`。
 * 幂等键由 `makeRunKey(capability, task, inputs)` 稳定生成（V106）。
 *
 * @dataSource remote
 */
export function GenerateView() {
  const projectId = useCurrentProjectId();
  const pushToast = useUiStore((state) => state.pushToast);

  const runs = useRuns(projectId);
  const cost = useCost(projectId);
  const capabilities = useCapabilities();
  const createRun = useCreateRun(projectId);
  const cancelRun = useCancelRun(projectId);
  const retryRun = useRetryRun(projectId);
  const model = useModelSettings();

  const [filter, setFilter] = useState<RunFilter>("all");
  const [composeOpen, setComposeOpen] = useState(false);
  /** 打开详情的运行 ID；`null` 表示关闭。见 RunDetail（单条轮询）。 */
  const [detailRunId, setDetailRunId] = useState<string | null>(null);

  const catalog = capabilities.data?.capabilities ?? [];

  /**
   * 运行解析结果（`GET /settings/model` 的 `runtime`）。
   *
   * **刻意不放进 `RemoteState` 的阻塞集合**：该端点是"仅本机访问"的管理接口，生产环境会被
   * 守卫直接拒绝。把它做成阻塞条件，一个本机守卫就会让整个生成中心打不开。
   * 读不到时为 `undefined` → **不猜**（既不置灰、也不声称可用），由后端在提交时显式拒绝，
   * 两个方向都不静默：能知道就前置拦住，不知道就让保证层说话。
   */
  const runtime = model.data?.runtime;
  const blocked = runtime ? runtimeBlocksGeneration(runtime) : false;
  const modelNote = runtime ? runtimeNote(runtime) : "";
  const [capability, setCapability] = useState("");
  const [writeTarget, setWriteTarget] = useState("");
  const [taskName, setTaskName] = useState("");

  const selected = catalog.find((item) => item.id === capability) ?? catalog[0] ?? null;
  const effectiveCapability = selected?.id ?? "";
  const writeOptions = selected?.allowed_write ?? [];

  const rows = useMemo(
    () => (runs.data ?? []).filter((run) => matchesFilter(run, filter)),
    [runs.data, filter],
  );

  const used = cost.data?.used ?? 0;
  const quota = cost.data?.quota ?? 0;
  const remaining = cost.data?.remaining ?? 0;
  const byModel = cost.data?.by_model ?? [];
  const usedRatio = quota > 0 ? Math.min(100, (used / quota) * 100) : 0;

  function submit(task: string, inputs: Record<string, string>) {
    const target = writeTarget || writeOptions[0] || undefined;
    createRun.mutate(
      {
        capability: effectiveCapability,
        task,
        inputs,
        writeTarget: target,
        idempotencyKey: makeRunKey(effectiveCapability, task, inputs),
      },
      {
        onSuccess: () =>
          pushToast({
            title: "已提交",
            detail: "先估算成本再断言额度，不足会整体拒绝而不会部分扣减",
            mark: "✓",
          }),
        onError: (error) =>
          pushToast({
            title: "提交失败",
            detail: error instanceof Error ? error.message : "未知错误",
            mark: "!",
          }),
      },
    );
  }

  return (
    <div className="page-scroll">
      <div className="page-width">
        <RemoteState queries={[runs, cost, capabilities]} title="生成中心加载失败">
          <header className="page-heading">
            <div>
              <span className="eyebrow">正式剧本驱动 · 先审核后发布</span>
              <h2>同一套故事与人物规则，继续驱动文本、图像和视频任务</h2>
            </div>
            <div className="toolbar-right">
              <Button
                variant="quiet"
                id="new-generation"
                disabled={blocked || catalog.length === 0}
                onClick={() => setComposeOpen(true)}
              >
                新建任务
              </Button>
              <Button
                variant="primary"
                id="batch-generate"
                disabled={blocked || createRun.isPending || catalog.length === 0}
                onClick={() => submit("批量生成队列任务", { scope: "queued" })}
              >
                {createRun.isPending ? "提交中…" : "批量生成"}
              </Button>
            </div>
          </header>

          {modelNote ? (
            <p className="inline-note" role="status">
              {modelNote}
            </p>
          ) : null}

          <section className="generation-header">
            <div className="usage-panel">
              <span className="eyebrow" style={{ color: "var(--gold)" }}>
                本月生成额度
              </span>
              <h2>
                已使用 {formatNumber(used)}，剩余 {formatNumber(remaining)}
              </h2>
              <div className="meter">
                <span style={{ width: `${usedRatio.toFixed(1)}%` }}></span>
              </div>
              <div className="usage-numbers">
                <strong>{usedRatio.toFixed(1)}%</strong>
                <span>配额 {formatNumber(quota)}</span>
              </div>
            </div>
            <div className="cost-breakdown">
              {byModel.length === 0 ? (
                <div className="cost-item">
                  <span>尚无用量</span>
                  <strong>0</strong>
                  <small>提交任务后按实际用量记账</small>
                </div>
              ) : (
                byModel.map((item) => (
                  <div className="cost-item" key={item.model}>
                    <span>{item.model}</span>
                    <strong>{formatNumber(item.units)}</strong>
                    <small>{item.calls} 次调用</small>
                  </div>
                ))
              )}
            </div>
          </section>

          <Panel title="能力目录" description="每个能力能写哪些产物、需要先通过哪个关口">
            {catalog.length === 0 ? (
              <p className="inline-note">未声明任何能力模块。</p>
            ) : (
              <ul className="activity-list">
                {catalog.map((item) => (
                  <li className="activity-item" key={item.id}>
                    <span className="mini-mark">◇</span>
                    <div>
                      <p>
                        {item.label} · <small>{item.id}</small>
                      </p>
                      <small>
                        可写入：{item.allowed_write.join(" / ") || "—"}
                        {item.required_gate ? ` · 前置关口 ${item.required_gate}` : ""}
                      </small>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <section>
            <div className="section-title">
              <div>
                <h3>生成任务</h3>
                <p>{rows.length} 项</p>
              </div>
              <div className="segment-control" aria-label="筛选生成任务">
                {FILTERS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={
                      option.value === filter ? "segment-button is-active" : "segment-button"
                    }
                    aria-pressed={option.value === filter}
                    onClick={() => setFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <DataTable<Run>
              variant="task"
              rows={rows}
              rowKey={(run) => run.id}
              empty={
                <EmptyState
                  title="还没有生成任务"
                  description="用上方「新建任务」发起一次能力运行，运行会先估算成本再断言额度。"
                />
              }
              columns={[
                {
                  key: "run",
                  header: "运行",
                  className: "rule-name",
                  render: (run) => (
                    <>
                      <strong>
                        {catalog.find((item) => item.id === run.capability)?.label ??
                          run.capability}
                      </strong>
                      <small>{run.run_key}</small>
                    </>
                  ),
                },
                {
                  key: "task",
                  header: "任务",
                  // 旧行或未带 task 的调用方为空，显示 "—" 而不是回落成能力名——
                  // 让别人填的任务名和系统推导出的名字看起来一样，比空着更难查。
                  render: (run) => run.task || "—",
                },
                {
                  key: "inputs",
                  header: "输入",
                  render: (run) => summarizeInputs(run.inputs),
                },
                {
                  key: "model",
                  header: "模型",
                  // provider 与模型名**并排显示**，不合并：`model` 记的是请求里的模型名，
                  // 而假 provider 会把它**原样回显**——只看模型名区分不出"真调用"与"假产出"，
                  // 而后者会真的写入产物并计入额度。
                  render: (run) => (
                    <>
                      <span>{run.model ?? "—"}</span>
                      <small>{providerLabel(run.provider)}</small>
                    </>
                  ),
                },
                {
                  key: "cost",
                  header: "额度",
                  render: (run) => formatNumber(run.cost_units),
                },
                {
                  key: "status",
                  header: "状态",
                  render: (run) => <RunStateLabel status={run.status} />,
                },
                {
                  key: "actions",
                  header: "操作",
                  render: (run) => (
                    <div className="toolbar-right">
                      <Button size="small" variant="quiet" onClick={() => setDetailRunId(run.id)}>
                        详情
                      </Button>
                      {run.status === "RETRYABLE_FAILED" ? (
                        <Button
                          size="small"
                          variant="quiet"
                          disabled={retryRun.isPending}
                          onClick={() => retryRun.mutate(run.id)}
                        >
                          重试
                        </Button>
                      ) : null}
                      {!RUN_FINAL_STATUSES.has(run.status) ? (
                        <Button
                          size="small"
                          variant="quiet"
                          disabled={cancelRun.isPending}
                          onClick={() => cancelRun.mutate(run.id)}
                        >
                          取消
                        </Button>
                      ) : null}
                    </div>
                  ),
                },
              ]}
            />
          </section>
        </RemoteState>
      </div>

      {/* 只在打开时挂载：让 RunDetail 的轮询状态随运行 ID 重置 */}
      {detailRunId ? (
        <RunDetail runId={detailRunId} catalog={catalog} onClose={() => setDetailRunId(null)} />
      ) : null}

      <Modal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        eyebrow="新建生成任务"
        title="从正式版本发起生成"
        footer={
          <>
            <Button variant="quiet" onClick={() => setComposeOpen(false)}>
              取消
            </Button>
            <Button
              variant="primary"
              disabled={
                blocked ||
                taskName.trim().length === 0 ||
                !effectiveCapability ||
                createRun.isPending
              }
              onClick={() => {
                submit(taskName.trim(), { scope: "single" });
                setTaskName("");
                setComposeOpen(false);
              }}
            >
              提交任务
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field id="gen-capability" label="能力模块" required>
            <Select
              id="gen-capability"
              value={effectiveCapability}
              options={catalog.map((item) => ({ value: item.id, label: item.label }))}
              onChange={(event) => {
                setCapability(event.target.value);
                setWriteTarget("");
              }}
            />
          </Field>
          <Field id="gen-name" label="任务名称" required>
            <TextInput
              id="gen-name"
              value={taskName}
              placeholder="例如：A01 婚房夜景 · 角色一致性定帧"
              onChange={(event) => setTaskName(event.target.value)}
            />
          </Field>
          {writeOptions.length > 0 ? (
            <Field id="gen-write" label="写入目标">
              <Select
                id="gen-write"
                value={writeTarget || writeOptions[0]}
                options={writeOptions.map((value) => ({ value, label: value }))}
                onChange={(event) => setWriteTarget(event.target.value)}
              />
            </Field>
          ) : null}
          {selected?.required_gate ? (
            <p className="inline-note">前置关口：{selected.required_gate}</p>
          ) : null}
          {selected && selected.forbidden_operations.length > 0 ? (
            <p className="inline-note">禁止操作：{selected.forbidden_operations.join(" · ")}</p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
